import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/auth/AuthContext";
import { useAuth } from "@/auth/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { CheckCircle, XCircle, ArrowsClockwise, ArrowRight, Lightning } from "@phosphor-icons/react";

const BillingSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { lang } = useT();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired | timeout
  const [plan, setPlan] = useState(null);
  const pollCountRef = useRef(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let canceled = false;
    const maxAttempts = 8;
    const interval = 2000;

    const poll = async () => {
      if (canceled) return;
      pollCountRef.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setPlan(data.plan);
          refreshUser();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus(data.payment_status === "failed" ? "failed" : "expired");
          return;
        }
        if (pollCountRef.current >= maxAttempts) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, interval);
      } catch (err) {
        if (pollCountRef.current >= maxAttempts) {
          setStatus("timeout");
        } else {
          setTimeout(poll, interval);
        }
      }
    };

    poll();
    return () => { canceled = true; };
  }, [sessionId, authLoading, user, navigate, refreshUser]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="max-w-md w-full">
        <Link to="/app" className="flex items-center justify-center gap-2 mb-10">
          <img src="/icons/icon-192.png" alt="TradeMind AI" className="w-8 h-8 rounded-md" />
          <span className="font-semibold tracking-tight text-zinc-50">TradeMind <span className="text-blue-500">AI</span></span>
        </Link>

        <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-8 text-center" data-testid="billing-result">
          {status === "polling" && (
            <>
              <ArrowsClockwise size={48} className="mx-auto text-blue-400 animate-spin mb-4" />
              <h2 className="text-2xl tracking-tight font-medium mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                {lang === "cs" ? "Ověřujeme tvoji platbu..." : "Verifying your payment..."}
              </h2>
              <p className="text-sm text-zinc-400">{lang === "cs" ? "Chvíli to potrvá." : "This will take a moment."}</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle weight="fill" size={56} className="mx-auto text-green-500 mb-4" />
              <h2 className="text-2xl tracking-tight font-medium mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                {lang === "cs" ? "Vítej v plánu" : "Welcome to"} <span className="text-blue-400">{plan}</span>!
              </h2>
              <p className="text-sm text-zinc-400 mb-6">
                {lang === "cs" ? "Platba prošla. Tvoje členství je aktivní 30 dní." : "Payment succeeded. Your membership is active for 30 days."}
              </p>
              <Link to="/app" data-testid="billing-back-app" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-md transition-colors">
                {lang === "cs" ? "Zpět do appky" : "Back to app"} <ArrowRight weight="bold" size={16} />
              </Link>
            </>
          )}
          {(status === "failed" || status === "expired" || status === "timeout") && (
            <>
              <XCircle weight="fill" size={56} className="mx-auto text-red-500 mb-4" />
              <h2 className="text-2xl tracking-tight font-medium mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                {status === "timeout"
                  ? (lang === "cs" ? "Platba se ještě zpracovává" : "Payment is still processing")
                  : (lang === "cs" ? "Platba se nepovedla" : "Payment failed")}
              </h2>
              <p className="text-sm text-zinc-400 mb-6">
                {status === "timeout"
                  ? (lang === "cs" ? "Pokud platba prošla, plán se brzy aktivuje. Zkontroluj email." : "If your payment was successful, your plan will activate shortly. Check your email.")
                  : (lang === "cs" ? "Zkus to znovu nebo nás kontaktuj." : "Please try again or contact support.")}
              </p>
              <Link to="/app/upgrade" data-testid="billing-retry" className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-100 font-medium px-5 py-2.5 rounded-md transition-colors">
                {lang === "cs" ? "Zkusit znovu" : "Try again"}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingSuccess;
